import { useState } from "react";
import CorreosParaIA from "@/components/correo/CorreosParaIA";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/ui/Toast";

type DestinoIA = "cliente" | "jimi" | "asesor";

/**
 * Botón "Generar comentario con IA" para el modal de envío de correos.
 *
 * Redacta un borrador para el cliente/proveedor/asesor a partir de la bitácora
 * interna del caso. El resultado entra al editor y queda 100% editable — nunca
 * se envía solo.
 *
 * **La guía vive AQUÍ, no en el editor.** Antes la instrucción para la IA se
 * escribía en la misma caja que se manda: quien le daba a Enviar en vez de ✨
 * le mandaba su propia instrucción al cliente. Pasó de verdad (2026-07-29). Lo
 * que está en el editor es SIEMPRE lo que se envía; lo de aquí nunca sale.
 *
 * Se gatea solo: si el usuario no tiene `ai_comentarios_enabled`, no renderiza
 * nada (así el modal solo lo monta sin lógica de permisos).
 */
export default function GenerarComentarioIA({
  casoId,
  destinatario,
  audiencia = "cliente",
  guiaHtml,
  onGenerado,
}: {
  casoId: number;
  destinatario: "cliente" | "jimi";
  /** Para quién es el correo: el cliente o alguien de Emprenet. */
  audiencia?: "cliente" | "interno";
  /** Lo que hay en el editor. NO se manda: solo alimenta «traer lo de arriba». */
  guiaHtml: string;
  onGenerado: (html: string) => void;
}) {
  const habilitado = useAuth((s) => s.user?.ai_comentarios_enabled);
  const toast = useToast();
  const [cargando, setCargando] = useState(false);
  const [guia, setGuia] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  // Correos del caso que la IA lee además de la bitácora. Arrancan apagados:
  // quien redacta decide qué material entra (ver `CorreosParaIA`).
  const [correosIA, setCorreosIA] = useState<number[]>([]);
  // El destino ya NO se elige aquí: lo manda el correo («¿Para quién es?»),
  // que es lo que decide también la PLANTILLA. Tener dos controles hacía que
  // la IA escribiera para el asesor mientras el correo seguía tuteándolo como
  // si el equipo fuera suyo (reporte de Joe, 2026-08-11).
  const destino: DestinoIA = audiencia === "interno" ? "asesor" : destinatario;

  if (!habilitado) return null;

  const enElEditor = htmlAtexto(guiaHtml);
  // Generar PISA lo que haya en el editor. Si está vacío no hay nada que
  // perder; si no, se pide confirmación (mismo criterio que el resto de la app).
  const pisaTexto = enElEditor !== "";

  async function generar() {
    setCargando(true);
    setConfirmando(false);
    try {
      const { texto } = await api.post<{ texto: string }>(
        `/api/casos/${casoId}/correos/generar-comentario`,
        { destinatario: destino, guia, correos_ids: correosIA },
      );
      onGenerado(textoAhtml(texto));
      toast.success("Comentario generado", "Revísalo y ajústalo antes de enviar.");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo generar", msg);
    } finally {
      setCargando(false);
    }
  }

  const etiquetaDestino =
    destino === "asesor"
      ? "un compañero de Emprenet"
      : destino === "cliente"
        ? "el cliente"
        : "el proveedor";

  return (
    <div className="rounded border border-accent-500/30 bg-accent-500/5 p-2">
      <p className="mb-1.5 text-[11px] text-zinc-500">
        Redacta para <strong className="text-zinc-300">{etiquetaDestino}</strong>, según a
        quién va dirigido el correo (se elige arriba).
      </p>

      <div className="mb-1.5">
        <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
          <span className="text-[11px] text-zinc-500">
            Qué quieres que diga <span className="text-zinc-600">(opcional)</span>
          </span>
          {enElEditor !== "" && guia === "" && (
            <button
              type="button"
              onClick={() => setGuia(enElEditor)}
              className="text-[10px] text-accent-400 hover:text-accent-300"
            >
              ↑ traer lo de arriba
            </button>
          )}
        </div>
        <textarea
          value={guia}
          onChange={(e) => {
            setGuia(e.target.value);
            setConfirmando(false);
          }}
          rows={2}
          disabled={cargando}
          placeholder="Ej.: menciona el contacto y los datos del equipo, nada más."
          className="input w-full font-normal text-xs"
          aria-label="Instrucción para la IA (no se envía)"
        />
        <p className="text-[10px] text-zinc-500 mt-0.5">
          Esto es la <strong>instrucción</strong>, no el correo: se queda aquí y{" "}
          <strong>nunca se manda</strong>.
        </p>
      </div>

      {/* Los correos del caso también son fuente de hechos: la bitácora sola
          deja fuera lo que dijo el cliente por correo. */}
      <CorreosParaIA
        casoId={casoId}
        seleccion={correosIA}
        onCambio={setCorreosIA}
        className="mb-1.5"
      />

      {confirmando ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void generar()}
            disabled={cargando}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
          >
            ✨ Sí, reemplazar lo de arriba
          </button>
          <button
            type="button"
            onClick={() => setConfirmando(false)}
            className="text-[11px] text-zinc-400 hover:text-zinc-200"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => (pisaTexto ? setConfirmando(true) : void generar())}
          disabled={cargando}
          className="inline-flex items-center gap-1.5 rounded-md border border-accent-500/40 bg-accent-500/10 px-2.5 py-1.5 text-xs text-accent-300 hover:bg-accent-500/20 disabled:opacity-50"
        >
          ✨ {cargando ? "Generando…" : "Generar comentario con IA"}
        </button>
      )}

      <p className="text-[10px] text-zinc-500 mt-1">
        Toma las notas de la bitácora, los contactos y los equipos del caso como
        base. El borrador entra al editor de arriba y queda editable; no se envía
        solo. Si le pides un dato que no tiene, lo marca como{" "}
        <code className="text-zinc-400">[falta: …]</code> en vez de inventarlo.
      </p>
    </div>
  );
}

/** HTML del editor → texto plano. */
function htmlAtexto(html: string): string {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim();
}

/** Texto de la IA (párrafos separados por línea en blanco) → HTML para TipTap. */
function textoAhtml(texto: string): string {
  const esc = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  return texto
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}
